import type { BrandingColors, PublicBranding } from '@busbook/shared';
import { bestOnColor, deriveDarkColors, hexToRgb } from '@busbook/shared';

/**
 * Turns a tenant's five brand colours into the full CSS-variable token set the
 * apps are built on (docs/04 §1). Every component reads these variables, so a
 * tenant's palette reaches the whole UI without a per-tenant CSS build.
 *
 * The derived tokens are the reason two tenants look unrelated rather than
 * "the same app in a different colour": borders, muted text and washes are all
 * computed from the tenant's own text and primary colours.
 */

export type ThemeTokens = Record<string, string>;

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Black or white — whichever is readable on the given background. */
export const onColor = bestOnColor;

export const FONT_STACKS: Record<string, string> = {
  system:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  Inter: '"Inter", system-ui, sans-serif',
  Roboto: '"Roboto", system-ui, sans-serif',
  Poppins: '"Poppins", system-ui, sans-serif',
  Lora: '"Lora", Georgia, serif',
};

export function fontStackFor(fontFamily: string): string {
  return FONT_STACKS[fontFamily] ?? FONT_STACKS.system!;
}

export function buildThemeTokens(colors: BrandingColors, fontFamily: string): ThemeTokens {
  return {
    '--color-primary': colors.primary,
    '--color-on-primary': onColor(colors.primary),
    '--color-secondary': colors.secondary,
    '--color-on-secondary': onColor(colors.secondary),
    '--color-bg': colors.background,
    '--color-surface': colors.surface,
    '--color-text': colors.text,

    // Derived from the tenant's own text colour so contrast holds in any palette.
    '--color-muted': rgba(colors.text, 0.62),
    '--color-faint': rgba(colors.text, 0.38),
    '--color-line': rgba(colors.text, 0.14),
    '--color-line-strong': rgba(colors.text, 0.28),
    '--color-primary-wash': rgba(colors.primary, 0.1),
    '--color-primary-edge': rgba(colors.primary, 0.35),
    '--shadow-card': `0 1px 2px ${rgba(colors.text, 0.06)}, 0 8px 24px ${rgba(colors.text, 0.06)}`,

    '--font-family': fontStackFor(fontFamily),
  };
}

/** Resolves LIGHT / DARK / AUTO against the viewer's OS preference. */
export function resolveColors(branding: PublicBranding, prefersDark: boolean): BrandingColors {
  const useDark =
    branding.themeMode === 'DARK' || (branding.themeMode === 'AUTO' && prefersDark);
  return useDark ? deriveDarkColors(branding.colors) : branding.colors;
}

/** Writes the tokens onto an element (normally `document.documentElement`). */
export function applyThemeTokens(element: HTMLElement, tokens: ThemeTokens): void {
  for (const [name, value] of Object.entries(tokens)) {
    element.style.setProperty(name, value);
  }
}

/** Google Fonts stylesheet URL for the tenant's chosen face, if it needs loading. */
export function webFontHref(fontFamily: string): string | null {
  if (fontFamily === 'system') return null;
  const weights = fontFamily === 'Lora' ? '400;500;600;700' : '400;500;600;700';
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@${weights}&display=swap`;
}
